import { isScreenViewLikeEvent } from './activityScreenLabels';
import { canonicalEventTypeForDisplay } from './activityEventLabels';

/**
 * Anlık aktivite feed'de gösterilmeyecek event tipleri.
 * Feed yalnızca iş aksiyonlarını gösterir; gezinti/teknik olaylar gizlenir.
 * KPI / analytics güncellemeleri etkilenmez; sadece feed listesi filtrelenir.
 */
const EXCLUDED = new Set([
  'session_start',
  'session_end',
  'sessionStart',
  'sessionEnd',
  'qr_code_generate',
  'qr_code_generated',
  'qrCodeGenerate',
  'qrCodeGenerated',
  'qr_generate',
  'QRCodeGenerate',
  'user_token_registered',
  'user_token_unregistered',
  'session_update',
  'sessionUpdate',
  'payment_method_selected',
  'paymentMethodSelected',
  // Onboarding adımları feed için gürültü kabul edilir.
  'onboarding_completed',
  'onboarding_continue_click',
  'onboarding_step_change',
  'onboarding_step_view',
  'onboardingCompleted',
  'onboardingContinueClick',
  'onboardingStepChange',
  'onboardingStepView',
  // Anlık aktivite feed'de kampanya görüntüleme gürültüsü istenmiyor
  'campaign_viewed',
  // İş aksiyonu olmayan düşük değerli / teknik olaylar
  'login_option_click',
  'loginOptionClick',
  'user_connection_established',
  'user_connection_closed',
  'userConnectionEstablished',
  'userConnectionClosed',
  // Kimlik doğrulanmadan önce oluşan, kullanıcı/isim taşımayan pre-auth olaylar
  'sms_verification_result',
  'smsVerificationResult',
  'sms_verification',
  'smsVerification',
  // Düşük değerli tıklama / deneme olayları — feed'de gürültü
  'login_attempt',
  'loginAttempt',
  'password_set_attempt',
  'passwordSetAttempt',
  'limit_increase_offer',
  'limitIncreaseOffer',
  'payment_result_home_click',
  'paymentResultHomeClick',
  'notification_bell_click',
  'notificationBellClick',
  'faq_open',
  'faqOpen',
]);

/** Düşük değerli analytics olayları — feed ve istatistik widget'ında gizlenir */
const EXCLUDED_ANALYTICS_UI = new Set([
  'screen_dwell',
  'empty_state_view',
  'empty_state_promo_view',
]);

/** Feed satırında "Detayları Göster" açılır paneli istenmeyen olaylar */
const FEED_WITHOUT_DETAIL_EXPAND = new Set([
  'qr_promo_popup_dismissed',
  'qr_promo_popup_shown',
  'notification_deleted',
]);

function isExcludedAnalyticsUiEvent(eventType) {
  if (!eventType) return false;
  const canonical = canonicalEventTypeForDisplay(eventType);
  return !!(canonical && EXCLUDED_ANALYTICS_UI.has(canonical));
}

/**
 * login_failed olaylarında hesap kilidi bilgisi varsa feed'de göster.
 * Backend alan adları farklı gelebileceği için olası varyasyonlar kontrol edilir.
 */
function isAccountLocked(activityData) {
  if (!activityData || typeof activityData !== 'object') return false;
  const candidates = [
    activityData.accountLocked,
    activityData.isAccountLocked,
    activityData.locked,
    activityData.isLocked,
    activityData.lockedOut,
    activityData.lockout,
    activityData.status,
    activityData.reason,
    activityData.message,
    activityData.data?.accountLocked,
    activityData.data?.isAccountLocked,
    activityData.data?.locked,
    activityData.data?.isLocked,
    activityData.data?.lockedOut,
    activityData.data?.lockout,
    activityData.eventData?.accountLocked,
    activityData.eventData?.isAccountLocked,
    activityData.eventData?.locked,
    activityData.eventData?.isLocked,
    activityData.eventData?.lockedOut,
    activityData.eventData?.lockout,
  ];

  for (const value of candidates) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) continue;
    }
    if (typeof value === 'string') {
      const s = value.trim().toLowerCase();
      if (['true', '1', 'locked', 'account_locked', 'kilitli', 'hesap_kilitli'].includes(s)) {
        return true;
      }
      if (s.includes('locked') || s.includes('kilit')) return true;
    }
  }
  return false;
}

/**
 * @param {string} [eventType]
 * @param {object|null} [activityData] - event.data / satır data (screen_view sayfa adı için)
 */
export function shouldExcludeFromActivityFeed(eventType, activityData = null) {
  if (isExcludedAnalyticsUiEvent(eventType)) {
    return true;
  }
  const canonical = eventType ? canonicalEventTypeForDisplay(eventType) : '';
  if (canonical && EXCLUDED.has(canonical)) {
    return true;
  }
  if (eventType && typeof eventType === 'string' && EXCLUDED.has(eventType)) {
    return true;
  }
  if (eventType === 'login_failed' || eventType === 'loginFailed' || eventType === 'failed_login') {
    // Sadece hesap kilitlendiyse göster, aksi halde gürültü kabul et.
    return !isAccountLocked(activityData);
  }
  // Feed yalnızca iş aksiyonlarını gösterir: tüm sayfa gezintisi (screen_view ve
  // PascalCase ekran bileşeni event'leri) gizlenir. Önemli aksiyonların kendi
  // event'leri vardır (ödeme, limit, taksit, işlemler...).
  if (isScreenViewLikeEvent(eventType, activityData)) {
    return true;
  }
  return false;
}

/** Aktivite istatistikleri widget'ında gösterilmeyecek olay tipleri */
export function shouldExcludeFromActivityStats(eventType) {
  return isExcludedAnalyticsUiEvent(eventType);
}

/**
 * @param {string} [eventType]
 * @returns {boolean} false ise feed'de detay paneli gösterilmez
 */
export function shouldShowDetailsInActivityFeed(eventType) {
  if (!eventType) return true;
  const canonical = canonicalEventTypeForDisplay(eventType);
  if (canonical && FEED_WITHOUT_DETAIL_EXPAND.has(canonical)) {
    return false;
  }
  return true;
}

/** Sync/API satırlarında event tipi farklı alan adlarıyla gelebilir */
export function activityRowShouldExcludeFromFeed(row) {
  if (!row || typeof row !== 'object') return false;
  const type =
    row.eventType ||
    row.action ||
    row.type ||
    (row.event && row.event.eventType);
  const data =
    row.data ||
    row.event?.data ||
    undefined;
  return shouldExcludeFromActivityFeed(type, data);
}
